import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError as observableThrowError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';

export interface ApiStatus {
  method: string;
  url: string;
  code: number;
  reason: string;
  messages: string[];
}

export const globalResponseInterceptor: HttpInterceptorFn = (request, next) => {
  const dataDog = inject(DataDogService);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => handleError(request, error, dataDog))
  );
};

/**
 * @param error
 * @param apiStatus
 *
 * clone the HttpErrorResponse with modified error
 */
function cloneErrorResponse(error: HttpErrorResponse, apiError: ApiStatus) {
  return new HttpErrorResponse({
    error: apiError,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url || undefined
  });
}

/**
 * @param error
 * @param apiStatus
 *
 * throw the modified HttpErrorResponse
 */
function throwError(error: HttpErrorResponse, apiStatus?: ApiStatus) {
  if (apiStatus) {
    return observableThrowError(cloneErrorResponse(error, apiStatus));
  }
  return observableThrowError(error);
}

/**
 * Throw consistent status objects from http calls.
 * Always throw a status object with a code and a reason
 */
function handleError(
  request: HttpRequest<unknown>,
  error: HttpErrorResponse,
  dataDog: DataDogService
) {
  const apiStatus: ApiStatus = {
    method: request.method ? request.method : 'unknown',
    url: error.url ? error.url : 'unknown',
    code: error.status === 0 || error.status ? Number(error.status) : 520,
    reason: '',
    messages: []
  };
  if (error.status !== 404) {
    dataDog.log(`API Error ${error.status}: ${request.method} ${error.url}`, 'error', {
      httpStatus: error.status,
      method: request.method,
      url: error.url,
      responseBody: error.error
    });
  }

  if (apiStatus.code === 401) {
    // ForgeRock SDK handles proactive token renewal; a 401 here means
    // the request genuinely lacks authorization, not an expired token.
    return throwError(error, apiStatus);
  }
  if (error instanceof HttpErrorResponse) {
    try {
      const body = error.error;
      // KP: this block is specifically for financial account service where the errponse pattern is a bit different.
      if (body?.finAcctServiceResponse?.apiStatus?.code != null) {
        apiStatus.reason = body?.finAcctServiceResponse?.apiStatus?.reason ?? 'Unknown Error';
        apiStatus.code = Number(body.finAcctServiceResponse.apiStatus.code);
        apiStatus.messages = body?.finAcctServiceResponse?.apiStatus?.messages ?? [];
        return throwError(body, apiStatus);
      }
      // most API error responses will return a code
      if (body?.status?.code != null) {
        apiStatus.reason = body.status.reason || body.status.message || 'Unknown Error';
        apiStatus.code = isNaN(Number(body.status.code)) ? 520 : Number(body.status.code);
        if (apiStatus.code !== 404) {
          apiStatus.messages = body?.status?.messages ?? body?.status?.errors ?? [];
          // log all messages
        }
        // trow the error without modification
        return throwError(body);
      }
      apiStatus.reason = `${error.statusText || ''} ${JSON.stringify(body)}`;
    } catch (err) {
      apiStatus.reason = String(err);
    }
  } else {
    const err = error as { message?: string };
    apiStatus.reason = err?.message ? String(err.message) : String(error);
  }
  return throwError(error, apiStatus);
}
